<script setup lang="ts">
/**
 * UiPillSelect — a compact, embeddable dropdown that renders as a pill: optional icon,
 * label, and a chevron. Designed to live inline inside other UI (text composers, toolbars,
 * inline editors) where a full UiSelect's field chrome would be too heavy.
 *
 * The trigger has no border by default and adapts to the surface it sits on. Colors are
 * fully customizable via `bgClass`, `textClass`, and `iconClass` so the consumer can tint
 * the pill to match the local context (e.g. a note composer's amber surface).
 */
import { computed, ref, watch, nextTick } from 'vue';
import { Select, createListCollection } from '@ark-ui/vue';
import UiIcon, { type UiIconProps } from './UiIcon.vue';

export interface UiPillSelectOption {
  value: string;
  label: string;
  icon?: UiIconProps['name'];
  description?: string;
}

export interface UiPillSelectProps {
  options: UiPillSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** xs: dense inline use, sm: default for composers/toolbars, md: standalone use, lg: button-aligned (32px tall — `min-h-8`, matches `UiButton size="sm"`) for use alongside small buttons in toolbars */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Background utility class for the trigger pill */
  bgClass?: string;
  /** Text utility class for the trigger label */
  textClass?: string;
  /** Color utility class for the leading icon and chevron */
  iconClass?: string;
  /** Hide the leading icon even if the option supplies one */
  hideIcon?: boolean;
  /** Tighten horizontal padding for embedding inside text fields */
  embedded?: boolean;
  /** ARIA label when no visible label is rendered */
  ariaLabel?: string;
  /** Override the popover content min-width (e.g. 'min-w-[200px]') */
  contentClass?: string;
}

const props = withDefaults(defineProps<UiPillSelectProps>(), {
  options: () => [],
  size: 'sm',
  hideIcon: false,
  embedded: false,
});

const model = defineModel<string>();

const selectedValue = ref<string[]>(model.value ? [model.value] : []);
const isOpen = ref<boolean>(false);

const collection = computed(() =>
  createListCollection({
    items: props.options,
    itemToValue: (item: UiPillSelectOption) => item.value,
    itemToString: (item: UiPillSelectOption) => item.label,
  }),
);

const selectedOption = computed<UiPillSelectOption | undefined>(() =>
  props.options.find((option) => option.value === selectedValue.value[0]),
);

const triggerLabel = computed(() => selectedOption.value?.label ?? props.placeholder ?? 'Select');
const triggerIcon = computed<UiIconProps['name'] | undefined>(() =>
  props.hideIcon ? undefined : selectedOption.value?.icon,
);

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'xs':
      return {
        trigger: 'text-[11px] leading-none gap-1',
        triggerPadding: props.embedded ? 'px-2 py-1' : 'px-2.5 py-1',
        radius: 'rounded',
        icon: 'h-3 w-3',
        chevron: 'h-3 w-3',
        item: 'text-[12px] px-2.5 py-1.5 gap-2',
        itemIcon: 'h-3.5 w-3.5',
      };
    case 'md':
      return {
        trigger: 'text-[14px] leading-none gap-1.5',
        triggerPadding: props.embedded ? 'px-3 py-1.5' : 'px-3.5 py-2',
        radius: 'rounded-md',
        icon: 'h-4 w-4',
        chevron: 'h-4 w-4',
        item: 'text-[14px] px-3.5 py-2 gap-2.5',
        itemIcon: 'h-4 w-4',
      };
    case 'lg':
      return {
        trigger: 'text-[13px] leading-none gap-1.5 min-h-8',
        triggerPadding: props.embedded ? 'px-2.5' : 'px-3',
        radius: 'rounded-md',
        icon: 'h-3.5 w-3.5',
        chevron: 'h-3.5 w-3.5',
        item: 'text-[13px] px-3 py-1.5 gap-2',
        itemIcon: 'h-4 w-4',
      };
    default:
      return {
        trigger: 'text-[12px] leading-none gap-1',
        triggerPadding: props.embedded ? 'px-2.5 py-1' : 'px-3 py-1.5',
        radius: 'rounded-md',
        icon: 'h-3.5 w-3.5',
        chevron: 'h-3.5 w-3.5',
        item: 'text-[13px] px-3 py-1.5 gap-2',
        itemIcon: 'h-4 w-4',
      };
  }
});

const triggerColorClasses = computed(() => ({
  bg: props.bgClass ?? 'bg-se-gray-100 hover:bg-se-gray-200',
  text: props.textClass ?? 'text-se-dark',
  icon: props.iconClass ?? 'text-se-gray-700',
}));

watch(model, (next) => {
  const incoming = next ? [next] : [];
  if (selectedValue.value[0] === incoming[0] && selectedValue.value.length === incoming.length) return;
  selectedValue.value = incoming;
}, { immediate: true });

watch(selectedValue, (next) => {
  const value = next[0];
  if (model.value === value) return;
  model.value = value;
});

watch(isOpen, (next) => {
  if (next && typeof window !== 'undefined') {
    nextTick(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    });
  }
});
</script>

<template>
  <Select.Root
    :collection="collection"
    v-model="selectedValue"
    :disabled="props.disabled"
    v-model:open="isOpen"
    :positioning="{ sameWidth: false }"
    lazyMount
  >
    <Select.Control class="inline-flex">
      <Select.Trigger
        class="inline-flex items-center font-medium transition-all duration-150 cursor-pointer outline-none whitespace-nowrap focus-visible:ring-2 focus-visible:ring-se-dark/20 disabled:cursor-not-allowed disabled:opacity-60"
        :class="[
          sizeClasses.trigger,
          sizeClasses.triggerPadding,
          sizeClasses.radius,
          triggerColorClasses.bg,
          triggerColorClasses.text,
        ]"
        :aria-label="props.ariaLabel"
      >
        <UiIcon
          v-if="triggerIcon"
          :name="triggerIcon"
          :class="[sizeClasses.icon, triggerColorClasses.icon, 'shrink-0']"
        />
        <Select.ValueText :placeholder="props.placeholder ?? 'Select'" class="truncate">
          {{ triggerLabel }}
        </Select.ValueText>
        <Select.Indicator class="shrink-0">
          <UiIcon
            name="chevron-down"
            :class="[
              sizeClasses.chevron,
              triggerColorClasses.icon,
              'transition-transform duration-150',
              isOpen ? 'rotate-180' : '',
            ]"
          />
        </Select.Indicator>
      </Select.Trigger>
    </Select.Control>
    <Teleport to="body" defer>
      <Select.Positioner>
        <Select.Content
          class="z-2000 my-1.5 overflow-hidden rounded-lg border border-se-gray-300 bg-white shadow-[0_8px_24px_-4px_rgba(15,23,42,0.16),0_2px_6px_-2px_rgba(15,23,42,0.08)] animate-in fade-in zoom-in-95 duration-100"
          :class="[
            props.contentClass ?? 'min-w-(--reference-width) max-w-[280px]',
          ]"
        >
          <div class="py-1">
            <Select.Item
              v-for="item in collection.items"
              :key="item.value"
              :item="item"
              class="flex cursor-pointer items-center text-se-dark transition-colors hover:bg-se-gray-100 data-highlighted:bg-se-gray-100 data-[state=checked]:bg-se-gray-50 data-[state=checked]:font-semibold"
              :class="sizeClasses.item"
            >
              <UiIcon
                v-if="item.icon"
                :name="item.icon"
                :class="[sizeClasses.itemIcon, 'shrink-0 text-se-gray-700']"
              />
              <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                <Select.ItemText class="truncate">{{ item.label }}</Select.ItemText>
                <span v-if="item.description" class="text-[11px] leading-snug text-se-gray-600">
                  {{ item.description }}
                </span>
              </div>
              <Select.ItemIndicator>
                <UiIcon name="check" class="ml-1 h-3 w-3 text-se-dark" />
              </Select.ItemIndicator>
            </Select.Item>
          </div>
        </Select.Content>
      </Select.Positioner>
    </Teleport>
    <Select.HiddenSelect />
  </Select.Root>
</template>
